'use client'

import type { ReactNode } from 'react'

import { css, styled } from 'styled-components'

import { Breakpoints } from '@b-stock/fe-core/design-system'

/**
 * Same horizontal inset and bottom spacing as a full-width carousel `Section`
 * ([carousel/ui.ts](home-portal/src/components/common/carousel/ui.ts)), without duplicating
 * its top padding — the dashboard shell and in-row carousel each apply their own top
 * spacing to align title rows.
 */
const BandSection = styled.div`
  width: 100%;
  padding: 0 1.5rem 1.5rem;

  @media ${Breakpoints.min.medium} {
    padding: 0 3rem 2rem;
  }
`

/** Horizontal space between listing cards in [Carousel.tsx](home-portal/src/components/common/carousel/Carousel.tsx) (`CarouselItem` margin-right). */
const carouselListingCardGap = '1.5rem'

const carouselHorizontalBleed = '1.375rem'

const defaultDashboardColumnWidth = '22rem'

const DashboardSlot = styled.div<{ $width: string; $fill: boolean }>`
  width: 100%;
  min-width: 0;

  @media ${Breakpoints.min.medium} {
    width: ${({ $width }) => $width};
    max-width: ${({ $width }) => $width};
    flex-shrink: 0;

    /*
     * Absolutely filling the slot removes the dashboard from the row's
     * intrinsic height, so the carousel column alone decides how tall the
     * band is and the dashboard stretches to match it exactly.
     */
    ${({ $fill }) =>
      $fill &&
      css`
        position: relative;
        align-self: stretch;

        & > * {
          position: absolute;
          inset: 0;
        }
      `}
  }
`

const CarouselSlot = styled.div`
  flex: 1;
  min-width: 0;
  padding-top: 3rem;

  &:empty {
    display: none;
  }

  /*
   * BandSection + BandInner already match Section + Container horizontal bounds.
   * Strip BaseCarousel Section padding here so we do not double-inset; top padding
   * is now owned by CarouselSlot so every carousel type (BaseCarousel section or
   * ShopMarketplaceCarousel div) gets consistent spacing.
   */
  section {
    padding: 0;

    @media ${Breakpoints.min.medium} {
      padding: 0;
    }
  }
`

/**
 * Same centering and max width as `Container` in carousel/ui.ts so this band shares
 * the same left/right bounds as “New Sellers”, “Best Deals”, etc.
 */
const BandInner = styled.div`
  width: 100%;
  max-width: var(--maxPageWidth);
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: ${carouselListingCardGap};

  @media ${Breakpoints.min.medium} {
    flex-direction: row;
    align-items: flex-start;
    /* Visually ~one inter-card gutter after carousel bleed; total band width unchanged (flex). */
    gap: calc(${carouselListingCardGap} + ${carouselHorizontalBleed});
  }
`

type HomeDashboardRowLayoutProps = {
  dashboardColumn: ReactNode
  carouselColumn: ReactNode
  /** md+ only; mobile always stacks full-width. */
  dashboardColumnWidth?: string
  /**
   * md+ only: stretch the dashboard column to the carousel-defined row height
   * instead of letting it size to its own content. The dashboard must handle
   * arbitrary heights internally (scroll/flex). Leave off when the carousel
   * column might be empty — a filled column has no intrinsic height to fall
   * back on.
   */
  fillDashboardColumn?: boolean
}

/** Dashboard column beside the primary carousel; stacks full-width on mobile. */
export function HomeDashboardRowLayout({
  dashboardColumn,
  carouselColumn,
  dashboardColumnWidth = defaultDashboardColumnWidth,
  fillDashboardColumn = false,
}: HomeDashboardRowLayoutProps) {
  return (
    <BandSection>
      <BandInner data-testid="home-dashboard-row-layout">
        <DashboardSlot
          $width={dashboardColumnWidth}
          $fill={fillDashboardColumn}
        >
          {dashboardColumn}
        </DashboardSlot>
        <CarouselSlot>{carouselColumn}</CarouselSlot>
      </BandInner>
    </BandSection>
  )
}
